import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { TicketDTO } from "../../types/dtos";
import type { AddUpdateTicketViewModel } from "../../types/viewModels";
import type { RecordId } from "@repo/types/ui/tables";
import { useApiAccess } from "@repo/hooks/contexts";
import {
  ticketKeys,
  type UseMutationSuccessOptions,
} from "@repo/types/queries";
import type { ExecutionResponse } from "@repo/types/responses";

export function useGetTicketById(ticketId: RecordId) {
  const { apiClient } = useApiAccess();

  return useQuery<TicketDTO>({
    queryKey: ticketKeys.details(ticketId),
    queryFn: async () => {
      const response = await apiClient.get<TicketDTO>(
        `/ticket/get/${ticketId}`,
      );
      return response.data;
    },
  });
}

export function useAddUpdateTicket(options?: UseMutationSuccessOptions) {
  const { apiClient } = useApiAccess();
  const queryClient = useQueryClient();

  return useMutation<ExecutionResponse, Error, AddUpdateTicketViewModel>({
    mutationFn: async (model: AddUpdateTicketViewModel) => {
      const response = await apiClient.post<ExecutionResponse>(
        `/ticket/addUpdate`,
        model,
      );
      return response.data;
    },
    onSuccess: (_, model) => {
      ticketKeys.invalidateDetails(queryClient, model.Id);

      if (options?.invalidate) {
        options.invalidate.forEach((invalidateFn) => invalidateFn(queryClient));
      }
    },
  });
}
